import { useEffect, useMemo } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import PropTypes from "prop-types";
import { useSew } from "../contexts/SewContext";
import SewCard from "../components/SewCard";
import NotFound from "../components/NotFound";
import StatusIndicator from "../components/StatusIndicator";
import { timeDifference } from "../utils/Utils";
//icons
import { CgDetailsMore } from "react-icons/cg";
import { GiOrbit } from "react-icons/gi";
import { HiOutlineCube } from "react-icons/hi2";
import { LuInfo } from "react-icons/lu";

const formatCoord = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num.toFixed(4) : "N/A";
};

// Altitude is stored in kilometres; display it in metres MSL (e.g. 0.015 -> "15 MSL").
const formatAltitude = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? `${Math.round(num * 1000)} MSL` : "N/A";
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {return "N/A";}
    if (typeof value === "object") {return JSON.stringify(value);}
    return String(value);
};

const Field = ({ label, value }) => (
    <Row style={{ marginTop: "10px" }}>
        <Col sm="4"><strong>{label}</strong></Col>
        <Col sm="8">{value}</Col>
    </Row>
);

Field.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node
};

const SewDossier = () => {
    const { id } = useParams();
    const { sewAssets, isLoading, fetchSewAssets } = useSew();

    const asset = sewAssets?.[id];

    useEffect(() => {
        if (!asset && !isLoading) {
            fetchSewAssets();
        }
    }, [asset, isLoading, fetchSewAssets]);

    // Derived: the asset is missing only once the list has loaded.
    const assetsLoaded = !isLoading && Object.keys(sewAssets).length > 0;
    const notFound = assetsLoaded && !asset;

    // Capabilities may arrive as a JSON object or a stringified JSON object.
    const capabilities = useMemo(() => {
        const raw = asset?.Capabilities;
        if (!raw) {return {};}
        if (typeof raw === "string") {
            try {
                return JSON.parse(raw);
            } catch {
                return { Capabilities: raw };
            }
        }
        return raw;
    }, [asset]);

    if (notFound) {
        return <NotFound id={id} />;
    }

    if (!asset) {
        return null;
    }

    // The "Image" capability is a URL we render as imagery below the map, not as a text field.
    const imageUrl = typeof capabilities.Image === "string" ? capabilities.Image : null;
    const capabilityEntries = Object.entries(capabilities).filter(([key]) => key !== "Image");

    return (
        <div style={{ padding: "20px" }}>
            <Helmet>
                <title>{`(${asset.Id}) ${asset.Name}`}</title>
                <meta name="sew-system-name" content={`SEW System: ${asset.Name}`} />
            </Helmet>

            <h2 style={{ marginBottom: "15px" }}>{asset.Id} - {asset.Name}</h2>

            <div style={{ fontStyle: "italic", color: "#ffc107", marginTop: "10px", marginBottom: "10px" }}>
                <LuInfo />
                This is publicly available information and was not provided through Intel channels.
            </div>
            <br />

            <Row className="flex-container">
                <Col md={8} className="mb-3 order-2 order-md-1">

                    {/* Overview */}
                    <Card className="mb-3">
                        <Card.Body>
                            <Card.Title style={{ color: "#ffc107" }}><CgDetailsMore /> Overview</Card.Title>
                            <Row>
                                <Col md={6}>
                                    <Field label="Classification" value={formatValue(asset.Classification)} />
                                    <Field label="Type" value={formatValue(asset.Type)} />
                                    <Field
                                        label="Status"
                                        value={
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                                <StatusIndicator status={asset.Status} />
                                                {formatValue(asset.Status)}
                                            </span>
                                        }
                                    />
                                    <Field label="Data Mode" value={formatValue(asset.DataMode)} />
                                </Col>
                                <Col md={6}>
                                    <Field label="NPS Base Id" value={formatValue(asset.NpsBaseId)} />
                                    <Field label="Country" value={formatValue(asset.CountryId)} />
                                    <Field label="Source" value={formatValue(asset.Source)} />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Location & Comms */}
                    <Card className="mb-3">
                        <Card.Body>
                            <Card.Title style={{ color: "#ffc107" }}><GiOrbit /> Location & Comms</Card.Title>
                            <Row>
                                <Col md={6}>
                                    <Field label="Latitude (°)" value={formatCoord(asset.Latitude)} />
                                    <Field label="Longitude (°)" value={formatCoord(asset.Longitude)} />
                                    <Field label="Altitude" value={formatAltitude(asset.Altitude)} />
                                </Col>
                                <Col md={6}>
                                    <Field
                                        label="Last Comms"
                                        value={asset.LastComms
                                            ? `[${timeDifference(asset.LastComms)}] ${asset.LastComms}`
                                            : "N/A"}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Capabilities */}
                    <Card className="mb-3">
                        <Card.Body>
                            <Card.Title style={{ color: "#ffc107" }}><HiOutlineCube /> Capabilities</Card.Title>
                            {capabilityEntries.length === 0 ? (
                                <>No Data</>
                            ) : (
                                <Row>
                                    {capabilityEntries.map(([key, value]) => (
                                        <Col md={6} key={key}>
                                            <Field label={key} value={formatValue(value)} />
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Record metadata */}
                    <Card className="mb-3">
                        <Card.Body>
                            <Card.Title style={{ color: "#ffc107" }}>Record</Card.Title>
                            <Row>
                                <Col md={6}>
                                    <Field label="Created" value={formatValue(asset.CreatedAt)} />
                                </Col>
                                <Col md={6}>
                                    <Field label="Updated" value={formatValue(asset.UpdatedAt)} />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Column */}
                <Col md={4} className="mb-4 order-1 order-md-2">
                    <SewCard asset={asset} imageUrl={imageUrl} />
                </Col>
            </Row>
        </div>
    );
};

export default SewDossier;
